import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingCart, Trash2, Plus, Minus, Loader2, CreditCard, Landmark, ArrowLeft, CheckCircle2,
} from "lucide-react";
import Topbar from "../components/Topbar";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api, formatApiError } from "../lib/api";
import { seatSubtotal, eur } from "../lib/pricing";
import { Button } from "../components/ui/button";

export default function Cart() {
  const { user } = useAuth();
  const { items, setSeats, removeItem, clear, isIndividual } = useCart();
  const navigate = useNavigate();
  const [method, setMethod] = useState("bonifico");
  const [busy, setBusy] = useState(false);

  const lines = items.map((i) => ({ ...i, line_total: seatSubtotal(i.price, i.seats) }));
  const subtotal = Math.round(lines.reduce((s, l) => s + l.line_total, 0) * 100) / 100;
  // Torna sempre alla vetrina interna all'area riservata (non al catalogo pubblico).
  const browsePath = isIndividual ? "/studente?tab=acquista" : "/azienda?tab=acquista";

  const checkout = async () => {
    if (!items.length) return;
    setBusy(true);
    try {
      await api.post("/orders", {
        items: items.map((i) => ({ course_id: i.course_id, seats: i.seats })),
      });
      clear();
      toast.success("Ordine creato: è in attesa di pagamento. Riceverai le istruzioni per completarlo.");
      navigate(user?.role === "azienda" ? "/azienda" : "/studente");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="cart-page" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          type="button"
          onClick={() => navigate(browsePath)}
          className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4 mb-6"
        >
          <ArrowLeft size={16} /> Continua a scegliere i corsi
        </button>

        <div className="flex items-center gap-2.5 mb-6">
          <ShoppingCart size={22} className="text-brand-blue" />
          <h1 className="text-2xl text-brand-ink">Il tuo carrello</h1>
        </div>

        {items.length === 0 ? (
          <div className="bg-white border border-brand-line rounded-sm p-10 text-center">
            <ShoppingCart size={36} className="mx-auto text-brand-blue/40 mb-3" />
            <p className="text-brand-muted mb-5">Il carrello è vuoto.</p>
            <Link to={browsePath} className="btn-primary inline-flex items-center gap-2">
              Sfoglia i corsi
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
            {/* Elenco articoli */}
            <div className="bg-white border border-brand-line rounded-sm divide-y divide-brand-line">
              {lines.map((l) => (
                <div key={l.course_id} className="p-5 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-brand-ink leading-snug">{l.title}</h3>
                    <p className="text-xs text-brand-muted mt-0.5">{eur(l.price)} a posto</p>

                    {isIndividual ? (
                      <p className="text-sm text-brand-muted mt-3">1 posto</p>
                    ) : (
                      <div className="mt-3 inline-flex items-center rounded-sm border border-brand-line overflow-hidden">
                        <button type="button" onClick={() => setSeats(l.course_id, l.seats - 1)}
                          disabled={l.seats <= 1}
                          className="h-9 w-9 flex items-center justify-center text-brand-blue hover:bg-brand-blue/5 disabled:opacity-40"
                          aria-label="Diminuisci">
                          <Minus size={15} />
                        </button>
                        <input type="number" min={1} value={l.seats}
                          onChange={(e) => setSeats(l.course_id, Number(e.target.value) || 1)}
                          className="h-9 w-14 text-center border-x border-brand-line outline-none" />
                        <button type="button" onClick={() => setSeats(l.course_id, l.seats + 1)}
                          className="h-9 w-9 flex items-center justify-center text-brand-blue hover:bg-brand-blue/5"
                          aria-label="Aumenta">
                          <Plus size={15} />
                        </button>
                        <span className="text-xs text-brand-muted px-3">posti</span>
                      </div>
                    )}
                  </div>

                  <div className="text-right shrink-0">
                    <div className="text-lg font-semibold text-brand-blue">{eur(l.line_total)}</div>
                    <button type="button" onClick={() => removeItem(l.course_id)}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 hover:underline">
                      <Trash2 size={13} /> Rimuovi
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Riepilogo + pagamento */}
            <aside className="bg-white border border-brand-line rounded-sm p-6">
              <h2 className="text-lg text-brand-ink mb-4">Riepilogo</h2>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm text-brand-muted">Totale (IVA esclusa)</span>
                <span className="text-2xl font-bold text-brand-blue">{eur(subtotal)}</span>
              </div>
              {!isIndividual && (
                <p className="text-[11px] text-brand-muted mb-4">
                  Sconti a volume applicati per corso: 1° a prezzo pieno, 2° −10%, dal 3° −20%.
                </p>
              )}

              <div className="mt-4">
                <p className="text-sm font-medium text-brand-ink mb-2">Metodo di pagamento</p>
                <div className="space-y-2">
                  <PaymentOption
                    icon={Landmark} label="Bonifico bancario"
                    desc="Riceverai i dati per il bonifico. L'accesso ai corsi è attivato a pagamento confermato."
                    value="bonifico" method={method} setMethod={setMethod} />
                  <PaymentOption
                    icon={CreditCard} label="Carta di credito"
                    desc="Pagamento immediato con carta." badge="Presto disponibile"
                    value="stripe" method={method} setMethod={setMethod} disabled />
                </div>
              </div>

              <Button onClick={checkout} disabled={busy}
                className="btn-primary w-full h-11 mt-6 inline-flex items-center justify-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={16} />
                  : <><CheckCircle2 size={16} /> Conferma ordine</>}
              </Button>
              <p className="text-[11px] text-brand-muted text-center mt-3">
                L'ordine viene creato in attesa di pagamento.
              </p>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}

function PaymentOption({ icon: Icon, label, desc, value, method, setMethod, badge, disabled }) {
  const selected = method === value;
  return (
    <button type="button" disabled={disabled}
      onClick={() => !disabled && setMethod(value)}
      className={`w-full text-left rounded-sm border p-3 flex items-start gap-3 transition-colors ${
        disabled ? "opacity-60 cursor-not-allowed border-brand-line"
        : selected ? "border-brand-blue bg-brand-blue/5" : "border-brand-line hover:border-brand-blue/40"
      }`}>
      <span className={`mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-sm ${selected ? "bg-brand-blue text-white" : "bg-brand-blue/5 text-brand-blue"}`}>
        <Icon size={16} />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold text-brand-ink">{label}</span>
          {badge && <span className="text-[10px] uppercase tracking-wide bg-brand-gold/10 text-brand-gold-deep border border-brand-gold/30 rounded-sm px-1.5 py-0.5">{badge}</span>}
        </span>
        <span className="block text-xs text-brand-muted mt-0.5">{desc}</span>
      </span>
    </button>
  );
}
